import { useCallback, useEffect, useState } from "react";
import { container } from "tsyringe";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import ProjectTeamRoleContributorsResponseResource from "common/resources/Project/Config/Team/Role/ProjectTeamRoleContributorsResponseResource";
import { upsert } from "common/utils/array";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { useProjectId } from "hooks/useProjectId";
import RoleService from "services/projects/RoleService";
import { useProjectTeamContext } from "../../contexts/ProjectTeam";
import CreateContributorModal from "./ContributorModal/CreateContributorModal";
import CreateRoleModal from "./RoleModal/CreateRoleModal";
import DeleteRoleModal from "./RoleModal/DeleteRoleModal";
import EditRoleModal from "./RoleModal/EditRoleModal";
import Roles from "./Roles";
import { ERoleModal, useRoleModal } from "./RoleModal";
import classes from "./classes.module.scss";

const roleService = container.resolve(RoleService);

export default function Team() {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();
	const { upsertRole, removeRole, upsertContributor, removeContributor, roles } = useRoles();
	const { selectedRole, isModalOpen, onCloseModal, onOpenCreateRoleModal, onOpenEditRoleModal, onOpenDeleteRoleModal, onOpenAddContributorModal } = useRoleModal();

	const onRoleCreated = useCallback(
		(role: ProjectTeamRoleContributorsResponseResource) => {
			upsertRole(role);
			onCloseModal();
			onOpenEditRoleModal(role);
		},
		[onCloseModal, onOpenEditRoleModal, upsertRole],
	);

	const onRoleEdited = useCallback(
		(role: ProjectTeamRoleContributorsResponseResource) => {
			upsertRole(role);
			onCloseModal();
		},
		[onCloseModal, upsertRole],
	);

	const onRoleDeleted = useCallback(
		(deletedRole: Pick<ProjectTeamRoleContributorsResponseResource, "id">) => {
			removeRole(deletedRole);
			onCloseModal();
		},
		[onCloseModal, removeRole],
	);

	const onContributorCreated = useCallback(
		async (contributor: ContributorResponseResource) => {
			selectedRole &&
				roleService
					.addContributor(projectId, projectTeamId, selectedRole.id, contributor.id)
					.then(() => upsertContributor(selectedRole, contributor))
					.then(onCloseModal);
		},
		[onCloseModal, projectId, projectTeamId, selectedRole, upsertContributor],
	);

	const onContributorAdded = useCallback(
		(contributor: ContributorResponseResource) => {
			selectedRole && onOpenEditRoleModal(upsertContributor(selectedRole, contributor));
		},
		[onOpenEditRoleModal, selectedRole, upsertContributor],
	);

	const onContributorEdited = useCallback(
		(contributor: ContributorResponseResource) => {
			selectedRole && onOpenEditRoleModal(upsertContributor(selectedRole, contributor));
		},
		[onOpenEditRoleModal, selectedRole, upsertContributor],
	);

	const onContributorRemoved = useCallback(
		(contributor: ContributorResponseResource) => {
			selectedRole && onOpenEditRoleModal(removeContributor(selectedRole, contributor));
		},
		[onOpenEditRoleModal, removeContributor, selectedRole],
	);

	return (
		<div className={classes["root"]}>
			<div className={classes["title-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.team.title}
				</Typography>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} onClick={onOpenCreateRoleModal}>
					{I18n.asset.pages.professional_space.tabs.team.create_role}
				</Button>
			</div>

			<Roles roles={roles} isEditing onAddContributor={onOpenAddContributorModal} onEditRole={onOpenEditRoleModal} onDeleteRole={onOpenDeleteRoleModal} />

			{isModalOpen(ERoleModal.ADD_CONTRIBUTOR) && selectedRole && <CreateContributorModal isOpen onCreated={onContributorCreated} onClose={onCloseModal} />}
			{isModalOpen(ERoleModal.CREATE_ROLE) && <CreateRoleModal isOpen onRoleCreated={onRoleCreated} onClose={onCloseModal} />}
			{isModalOpen(ERoleModal.EDIT_ROLE) && selectedRole && (
				<EditRoleModal
					isOpen
					role={selectedRole}
					onRoleEdited={onRoleEdited}
					onContributorAdded={onContributorAdded}
					onContributorEdited={onContributorEdited}
					onContributorRemoved={onContributorRemoved}
					onClose={onCloseModal}
				/>
			)}
			{isModalOpen(ERoleModal.DELETE_ROLE) && selectedRole && <DeleteRoleModal isOpen role={selectedRole} onDeleted={onRoleDeleted} onClose={onCloseModal} />}
		</div>
	);
}

const useRoles = () => {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();
	const [roles, setRoles] = useState<ProjectTeamRoleContributorsResponseResource[]>([]);

	const fetchRoles = useCallback(async () => {
		return roleService
			.getAllByProjectTeamId(projectId, projectTeamId)
			.then(setRoles)
			.catch((error) => {
				console.error(error);
				setRoles([]);
			});
	}, [projectId, projectTeamId]);

	const upsertRole = useCallback((role: ProjectTeamRoleContributorsResponseResource) => {
		setRoles((roles) => upsert(roles, role, (r) => r.id === role.id));
		return role;
	}, []);

	const removeRole = useCallback((deletedRole: Pick<ProjectTeamRoleContributorsResponseResource, "id">) => {
		setRoles((roles) => roles.filter((r) => r.id !== deletedRole.id));
		return deletedRole;
	}, []);

	const upsertContributor = useCallback(
		(role: ProjectTeamRoleContributorsResponseResource, contributor: ContributorResponseResource) => {
			return upsertRole(
				ProjectTeamRoleContributorsResponseResource.hydrate<ProjectTeamRoleContributorsResponseResource>({
					...role,
					contributors: upsert(role.contributors, contributor, (c) => c.id === contributor.id),
				}),
			);
		},
		[upsertRole],
	);

	const removeContributor = useCallback(
		(role: ProjectTeamRoleContributorsResponseResource, deletedContributor: Pick<ContributorResponseResource, "id">) => {
			return upsertRole(
				ProjectTeamRoleContributorsResponseResource.hydrate<ProjectTeamRoleContributorsResponseResource>({
					...role,
					contributors: role.contributors.filter((c) => c.id !== deletedContributor.id),
				}),
			);
		},
		[upsertRole],
	);

	useEffect(() => {
		fetchRoles();
	}, [fetchRoles]);

	return { roles, removeRole, upsertRole, removeContributor, upsertContributor };
};
